/**
 * Ambassador Program Admin Page
 *
 * Full admin panel for managing the ambassador program:
 * - Tab 1: Ambassadors — list, create, edit, status management
 * - Tab 2: Referrals — stats overview + referral table
 * - Tab 3: Milestones & Payouts — pending milestones + payout management
 *
 * Copyright: Guardrail Technologies - PROPRIETARY
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Copy,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { PageBackground } from '@/components/ui/PageLayout';
import { PageSubHeader } from '@/components/ui/PageSubHeader';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { useAuth } from '@/contexts/AuthContext';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  useAmbassadors,
  useCreateAmbassador,
  useUpdateAmbassador,
  useReferralStats,
  usePayouts,
  useUpdatePayout,
  type Ambassador,
  type AmbassadorStatus,
  type Payout,
  type PayoutStatus,
} from '@/hooks/useAmbassadors';

// ---------------------------------------------------------------------------
// Status badge helpers
// ---------------------------------------------------------------------------

function AmbassadorStatusBadge({ status }: { status: AmbassadorStatus }) {
  const variants: Record<AmbassadorStatus, string> = {
    active: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    suspended: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    deactivated: 'bg-red-500/15 text-red-400 border-red-500/30',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${variants[status]}`}
    >
      {status}
    </span>
  );
}


function PayoutStatusBadge({ status }: { status: PayoutStatus }) {
  const variants: Record<PayoutStatus, string> = {
    pending: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
    processing: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    completed: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    failed: 'bg-red-500/15 text-red-400 border-red-500/30',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${variants[status]}`}
    >
      {status}
    </span>
  );
}


function fmtDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-CA');
}

// ---------------------------------------------------------------------------
// Create Ambassador Dialog
// ---------------------------------------------------------------------------

function CreateAmbassadorDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const createMutation = useCreateAmbassador();

  function handleSubmit() {
    if (!name.trim() || !email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    createMutation.mutate(
      { name: name.trim(), email: email.trim() },
      {
        onSuccess: () => {
          toast.success('Ambassador created');
          setName('');
          setEmail('');
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Failed to create ambassador');
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-700 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">Create Ambassador</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <label className="text-sm text-zinc-400">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-zinc-400">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ambassador@example.com"
              className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-zinc-700 text-zinc-300"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Ambassador Row (expandable)
// ---------------------------------------------------------------------------

function AmbassadorRow({ ambassador }: { ambassador: Ambassador }) {
  const [expanded, setExpanded] = useState(false);
  const [editName, setEditName] = useState(ambassador.name);
  const [editStatus, setEditStatus] = useState<AmbassadorStatus>(ambassador.status);
  const [editing, setEditing] = useState(false);
  const [, copy] = useCopyToClipboard();
  const updateMutation = useUpdateAmbassador();

  function handleSave() {
    updateMutation.mutate(
      { id: ambassador.id, data: { name: editName, status: editStatus } },
      {
        onSuccess: () => {
          toast.success('Ambassador updated');
          setEditing(false);
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Update failed');
        },
      },
    );
  }

  return (
    <>
      <TableRow
        className="border-zinc-800 hover:bg-zinc-800/40 cursor-pointer"
        onClick={() => setExpanded((x) => !x)}
      >
        <TableCell className="text-zinc-200 font-medium">{ambassador.name}</TableCell>
        <TableCell className="text-zinc-400 text-sm">{ambassador.email}</TableCell>
        <TableCell>
          <div className="flex items-center gap-1.5">
            <code className="text-xs text-emerald-400 bg-emerald-900/20 px-1.5 py-0.5 rounded font-mono">
              {ambassador.referralCode}
            </code>
            <button
              onClick={(e) => {
                e.stopPropagation();
                copy(ambassador.referralCode);
                toast.success('Referral code copied');
              }}
              className="text-zinc-500 hover:text-zinc-300 transition-colors"
              aria-label="Copy referral code"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        </TableCell>
        <TableCell>
          <AmbassadorStatusBadge status={ambassador.status} />
        </TableCell>
        <TableCell className="text-zinc-300 text-right tabular-nums">
          {ambassador.totalReferrals}
        </TableCell>
        <TableCell className="text-zinc-300 text-right tabular-nums">
          {ambassador.qualifiedReferrals}
        </TableCell>
        <TableCell className="text-zinc-500 text-sm">{fmtDate(ambassador.createdAt)}</TableCell>
        <TableCell className="text-zinc-500 w-6">
          {expanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow className="border-zinc-800 bg-zinc-900/60">
          <TableCell colSpan={8} className="py-4 px-6">
            {editing ? (
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-zinc-500">Name</label>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white h-8 text-sm w-48"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-zinc-500">Status</label>
                  <Select
                    value={editStatus}
                    onValueChange={(v) => setEditStatus(v as AmbassadorStatus)}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-8 text-sm w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="active" className="text-emerald-400">
                        active
                      </SelectItem>
                      <SelectItem value="suspended" className="text-amber-400">
                        suspended
                      </SelectItem>
                      <SelectItem value="deactivated" className="text-red-400">
                        deactivated
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white h-8"
                >
                  {updateMutation.isPending ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(false)}
                  className="border-zinc-700 text-zinc-300 h-8"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <p className="text-xs text-zinc-500">
                  ID: <span className="text-zinc-400 font-mono">{ambassador.id}</span>
                </p>
                {ambassador.keycloakUserId && (
                  <p className="text-xs text-zinc-500">
                    KC:{' '}
                    <span className="text-zinc-400 font-mono">{ambassador.keycloakUserId}</span>
                  </p>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditing(true);
                  }}
                  className="border-zinc-700 text-zinc-300 h-7 text-xs"
                >
                  Edit
                </Button>
              </div>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Tab 1: Ambassadors
// ---------------------------------------------------------------------------

function AmbassadorsTab() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<AmbassadorStatus | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading, isError } = useAmbassadors(page, statusFilter);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Select
            value={statusFilter ?? 'all'}
            onValueChange={(v) =>
              setStatusFilter(v === 'all' ? undefined : (v as AmbassadorStatus))
            }
          >
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300 h-8 text-sm w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="all" className="text-zinc-300">
                All statuses
              </SelectItem>
              <SelectItem value="active" className="text-emerald-400">
                active
              </SelectItem>
              <SelectItem value="suspended" className="text-amber-400">
                suspended
              </SelectItem>
              <SelectItem value="deactivated" className="text-red-400">
                deactivated
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          size="sm"
          onClick={() => setCreateOpen(true)}
          className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5"
        >
          <UserPlus className="w-4 h-4" />
          Create Ambassador
        </Button>
      </div>

      <div className="rounded-lg border border-zinc-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-400">Name</TableHead>
              <TableHead className="text-zinc-400">Email</TableHead>
              <TableHead className="text-zinc-400">Referral Code</TableHead>
              <TableHead className="text-zinc-400">Status</TableHead>
              <TableHead className="text-zinc-400 text-right">Total Refs</TableHead>
              <TableHead className="text-zinc-400 text-right">Qualified</TableHead>
              <TableHead className="text-zinc-400">Created</TableHead>
              <TableHead className="w-6" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i} className="border-zinc-800">
                  {Array.from({ length: 8 }).map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full bg-zinc-800" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {isError && (
              <TableRow className="border-zinc-800">
                <TableCell colSpan={8} className="text-center py-8 text-red-400">
                  Failed to load ambassadors.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && data?.data.length === 0 && (
              <TableRow className="border-zinc-800">
                <TableCell colSpan={8} className="text-center py-10 text-zinc-500">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No ambassadors yet.</p>
                  <p className="text-xs mt-1">Create one to get started.</p>
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              !isError &&
              data?.data.map((amb) => <AmbassadorRow key={amb.id} ambassador={amb} />)}
          </TableBody>
        </Table>
      </div>

      {data && data.total > 20 && (
        <div className="flex items-center justify-between text-sm text-zinc-500">
          <span>
            {(page - 1) * 20 + 1}–{Math.min(page * 20, data.total)} of {data.total}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-zinc-700 text-zinc-300 h-7"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-zinc-700 text-zinc-300 h-7"
              disabled={page * 20 >= data.total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <CreateAmbassadorDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2: Referrals
// ---------------------------------------------------------------------------

function ReferralsTab() {
  const { data: stats, isLoading: statsLoading } = useReferralStats();

  const statCards = [
    { label: 'Total Referrals', value: stats?.total, icon: Users, color: 'text-blue-400' },
    {
      label: 'Qualified',
      value: stats?.qualified,
      icon: CheckCircle2,
      color: 'text-emerald-400',
    },
    { label: 'Pending', value: stats?.pending, icon: Clock, color: 'text-amber-400' },
    {
      label: 'Ineligible',
      value: stats?.ineligible,
      icon: XCircle,
      color: 'text-red-400',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map(({ label, value, icon: Icon, color }) => (
          <Card key={label} className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-500">{label}</p>
                  {statsLoading ? (
                    <Skeleton className="h-7 w-12 mt-1 bg-zinc-800" />
                  ) : (
                    <p className={`text-2xl font-bold ${color}`}>{value ?? 0}</p>
                  )}
                </div>
                <Icon className={`w-6 h-6 ${color} opacity-60`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Info note */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="py-8 text-center text-zinc-500">
          <p className="text-sm">
            Referral details are available per-ambassador. Select an ambassador in the Ambassadors
            tab and expand their row to view referral history.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payout row (inline status update)
// ---------------------------------------------------------------------------

function PayoutRow({ payout }: { payout: Payout }) {
  const updateMutation = useUpdatePayout();

  function handleStatusChange(status: PayoutStatus) {
    updateMutation.mutate(
      { id: payout.id, data: { status } },
      {
        onSuccess: () => toast.success('Payout updated'),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Update failed'),
      },
    );
  }

  return (
    <TableRow className="border-zinc-800 hover:bg-zinc-800/40">
      <TableCell className="text-zinc-200 font-medium">{payout.ambassadorName}</TableCell>
      <TableCell className="text-zinc-300 tabular-nums">
        {payout.currency} {payout.amount.toFixed(2)}
      </TableCell>
      <TableCell className="text-zinc-400 text-sm">{payout.method}</TableCell>
      <TableCell>
        <PayoutStatusBadge status={payout.status} />
      </TableCell>
      <TableCell className="text-zinc-500 text-sm">{fmtDate(payout.createdAt)}</TableCell>
      <TableCell>
        {payout.status === 'pending' && (
          <Button
            size="sm"
            variant="outline"
            className="border-amber-700 text-amber-400 hover:bg-amber-900/20 h-7 text-xs"
            onClick={() => handleStatusChange('processing')}
            disabled={updateMutation.isPending}
          >
            Process
          </Button>
        )}
        {payout.status === 'processing' && (
          <div className="flex gap-1.5">
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-500 text-white h-7 text-xs"
              onClick={() => handleStatusChange('completed')}
              disabled={updateMutation.isPending}
            >
              Complete
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-red-700 text-red-400 hover:bg-red-900/20 h-7 text-xs"
              onClick={() => handleStatusChange('failed')}
              disabled={updateMutation.isPending}
            >
              Fail
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Tab 3: Milestones & Payouts
// ---------------------------------------------------------------------------

// Placeholder milestone list — fetches from all ambassadors would require
// an aggregated endpoint. This section shows payouts and a note.
function MilestonesPayoutsTab() {
  const [payoutPage, setPayoutPage] = useState(1);
  const [payoutStatus, setPayoutStatus] = useState<PayoutStatus | undefined>();
  const { data: payouts, isLoading: payoutsLoading } = usePayouts(payoutPage, payoutStatus);

  return (
    <div className="space-y-8">
      {/* Pending Milestones section */}
      <div>
        <h3 className="text-sm font-semibold text-zinc-300 mb-3">Pending Milestone Claims</h3>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="py-6 text-center text-zinc-500 text-sm">
            Milestone claims are submitted by ambassadors and appear here when status is{' '}
            <span className="text-amber-400">claimed</span> or{' '}
            <span className="text-amber-400">verified</span>. Select an ambassador from the
            Ambassadors tab to view and act on their milestones directly.
          </CardContent>
        </Card>
      </div>

      {/* Payouts section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-zinc-300">Payouts</h3>
          <Select
            value={payoutStatus ?? 'all'}
            onValueChange={(v) =>
              setPayoutStatus(v === 'all' ? undefined : (v as PayoutStatus))
            }
          >
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300 h-8 text-sm w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="all" className="text-zinc-300">
                All statuses
              </SelectItem>
              <SelectItem value="pending" className="text-zinc-400">
                pending
              </SelectItem>
              <SelectItem value="processing" className="text-amber-400">
                processing
              </SelectItem>
              <SelectItem value="completed" className="text-emerald-400">
                completed
              </SelectItem>
              <SelectItem value="failed" className="text-red-400">
                failed
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-lg border border-zinc-800 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-400">Ambassador</TableHead>
                <TableHead className="text-zinc-400">Amount</TableHead>
                <TableHead className="text-zinc-400">Method</TableHead>
                <TableHead className="text-zinc-400">Status</TableHead>
                <TableHead className="text-zinc-400">Date</TableHead>
                <TableHead className="w-36" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {payoutsLoading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i} className="border-zinc-800">
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full bg-zinc-800" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {!payoutsLoading && (!payouts?.data || payouts.data.length === 0) && (
                <TableRow className="border-zinc-800">
                  <TableCell colSpan={6} className="text-center py-10 text-zinc-500">
                    <p>No payouts found.</p>
                  </TableCell>
                </TableRow>
              )}
              {!payoutsLoading &&
                payouts?.data.map((p) => <PayoutRow key={p.id} payout={p} />)}
            </TableBody>
          </Table>
        </div>

        {payouts && payouts.total > 20 && (
          <div className="flex items-center justify-between text-sm text-zinc-500 mt-3">
            <span>
              {(payoutPage - 1) * 20 + 1}–{Math.min(payoutPage * 20, payouts.total)} of{' '}
              {payouts.total}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="border-zinc-700 text-zinc-300 h-7"
                disabled={payoutPage === 1}
                onClick={() => setPayoutPage((p) => p - 1)}
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-zinc-700 text-zinc-300 h-7"
                disabled={payoutPage * 20 >= payouts.total}
                onClick={() => setPayoutPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AmbassadorPage() {
  useDocumentTitle('Ambassador Program');
  const { authenticated, loading, user } = useAuth();

  if (loading) {
    return null;
  }

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  const isAdmin = user?.roles?.includes('admin');
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <AppHeader />
      <PageBackground>
        <PageSubHeader
          title="Ambassador Program"
          subtitle="Manage ambassadors, track referrals, and process milestones and payouts."
        />

        <div className="container mx-auto px-4 py-6 max-w-7xl">
          <Tabs defaultValue="ambassadors" className="space-y-6">
            <TabsList className="bg-zinc-900 border border-zinc-800">
              <TabsTrigger
                value="ambassadors"
                className="data-[state=active]:bg-zinc-700 data-[state=active]:text-white text-zinc-400"
              >
                Ambassadors
              </TabsTrigger>
              <TabsTrigger
                value="referrals"
                className="data-[state=active]:bg-zinc-700 data-[state=active]:text-white text-zinc-400"
              >
                Referrals
              </TabsTrigger>
              <TabsTrigger
                value="milestones"
                className="data-[state=active]:bg-zinc-700 data-[state=active]:text-white text-zinc-400"
              >
                Milestones & Payouts
              </TabsTrigger>
            </TabsList>

            <TabsContent value="ambassadors">
              <AmbassadorsTab />
            </TabsContent>
            <TabsContent value="referrals">
              <ReferralsTab />
            </TabsContent>
            <TabsContent value="milestones">
              <MilestonesPayoutsTab />
            </TabsContent>
          </Tabs>
        </div>
      </PageBackground>
    </>
  );
}
